/**
 * Repeat cleans: pure date maths and edit-scope rules.
 *
 * The database (extend_recurring_series etc., migration
 * 20261101222418_recurring_visits.sql) is the source of truth for which visits
 * exist. This module mirrors the same rules in the browser so the UI can
 * preview dates, describe a schedule and explain what an edit will touch.
 * No React / Supabase imports so it can be unit-tested directly
 * (tests/recurrence.spec.ts).
 *
 * Dates are 'yyyy-MM-dd' strings handled as UTC calendar days, so UK clock
 * changes (or the browser's timezone) can never shift a visit by a day.
 */

export type RecurrenceFrequency = 'weekly' | 'fortnightly' | 'four_weekly' | 'monthly';
export type SeriesStatus = 'pending' | 'active' | 'paused' | 'ended';

export const FREQUENCY_OPTIONS: { value: RecurrenceFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'four_weekly', label: 'Every 4 weeks' },
  { value: 'monthly', label: 'Monthly' },
];

export const FREQUENCY_LABELS: Record<RecurrenceFrequency, string> = {
  weekly: 'Weekly',
  fortnightly: 'Fortnightly',
  four_weekly: 'Every 4 weeks',
  monthly: 'Monthly',
};

export const isRecurrenceFrequency = (value: unknown): value is RecurrenceFrequency =>
  value === 'weekly' || value === 'fortnightly' || value === 'four_weekly' || value === 'monthly';

/**
 * Map free-text frequencies (enquiries, the booking form) onto a series
 * frequency. Mirrors public.recurring_frequency_key. One-off → null.
 */
export function frequencyFromText(value: string | null | undefined): RecurrenceFrequency | null {
  const key = (value ?? '').trim().toLowerCase().replace(/[\s_-]+/g, ' ');
  switch (key) {
    case 'weekly':
    case 'every week':
      return 'weekly';
    case 'fortnightly':
    case 'biweekly':
    case 'bi weekly':
    case 'every 2 weeks':
    case 'every two weeks':
      return 'fortnightly';
    case 'four weekly':
    case '4 weekly':
    case 'every 4 weeks':
    case 'every four weeks':
      return 'four_weekly';
    case 'monthly':
    case 'every month':
      return 'monthly';
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Calendar-day helpers (UTC, DST-proof)
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 'yyyy-MM-dd' → whole days since 1970-01-01. Throws on malformed input. */
export function toDayNumber(date: string): number {
  const m = ISO.exec(date);
  if (!m) throw new Error(`Invalid date: ${date}`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / DAY_MS;
}

export function fromDayNumber(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

export const addDaysISO = (date: string, days: number) => fromDayNumber(toDayNumber(date) + days);

/** 0 = Sunday … 6 = Saturday (same as Postgres extract(dow) and team_availability). */
export const weekdayOf = (date: string) => new Date(toDayNumber(date) * DAY_MS).getUTCDay();

const daysInMonth = (year: number, month0: number) => new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();

/** Week-of-month for a monthly series: 1–4, or -1 for "last" (anchor on day 29–31). */
export function monthlyOrdinal(anchor: string): number {
  const day = Number(anchor.slice(8, 10));
  return day > 28 ? -1 : Math.floor((day - 1) / 7) + 1;
}

/** The nth (1–4) or last (-1) weekday of a month. */
export function nthWeekdayOfMonth(year: number, month0: number, weekday: number, ordinal: number): string {
  if (ordinal === -1) {
    const last = daysInMonth(year, month0);
    const lastDow = new Date(Date.UTC(year, month0, last)).getUTCDay();
    const day = last - ((lastDow - weekday + 7) % 7);
    return fromDayNumber(Date.UTC(year, month0, day) / DAY_MS);
  }
  const firstDow = new Date(Date.UTC(year, month0, 1)).getUTCDay();
  const day = 1 + ((weekday - firstDow + 7) % 7) + (ordinal - 1) * 7;
  return fromDayNumber(Date.UTC(year, month0, day) / DAY_MS);
}

const STEP_DAYS: Record<Exclude<RecurrenceFrequency, 'monthly'>, number> = {
  weekly: 7,
  fortnightly: 14,
  four_weekly: 28,
};

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  /** The date the pattern is counted from (weekday, fortnight phase, week-of-month). */
  anchorDate: string;
  /** Last date a visit may fall on (inclusive). */
  endDate?: string | null;
}

/**
 * Every date the rule produces in [from, to] (inclusive), never before the
 * anchor or after the end date. Mirrors public.recurring_series_dates.
 */
export function occurrenceDates(rule: RecurrenceRule, from: string, to: string): string[] {
  const start = Math.max(toDayNumber(from), toDayNumber(rule.anchorDate));
  let end = toDayNumber(to);
  if (rule.endDate) end = Math.min(end, toDayNumber(rule.endDate));
  if (start > end) return [];

  const out: string[] = [];
  if (rule.frequency === 'monthly') {
    const weekday = weekdayOf(rule.anchorDate);
    const ordinal = monthlyOrdinal(rule.anchorDate);
    const s = new Date(start * DAY_MS);
    let year = s.getUTCFullYear();
    let month = s.getUTCMonth();
    for (;;) {
      if (Date.UTC(year, month, 1) / DAY_MS > end) break;
      const d = nthWeekdayOfMonth(year, month, weekday, ordinal);
      const n = toDayNumber(d);
      if (n >= start && n <= end) out.push(d);
      month += 1;
      if (month === 12) {
        month = 0;
        year += 1;
      }
    }
    return out;
  }

  const step = STEP_DAYS[rule.frequency];
  const anchor = toDayNumber(rule.anchorDate);
  for (let d = anchor + Math.ceil((start - anchor) / step) * step; d <= end; d += step) {
    out.push(fromDayNumber(d));
  }
  return out;
}

/** The next `count` visits on or after `from`. */
export function nextOccurrences(rule: RecurrenceRule, from: string, count: number): string[] {
  if (count <= 0) return [];
  const horizon = addDaysISO(from, count * 35 + 35);
  return occurrenceDates(rule, from, horizon).slice(0, count);
}

/** Date of the nth visit (1 = the anchor). Turns "after N visits" into an end date. */
export function nthOccurrenceDate(frequency: RecurrenceFrequency, anchorDate: string, n: number): string {
  const dates = nextOccurrences({ frequency, anchorDate }, anchorDate, Math.max(1, n));
  return dates[dates.length - 1];
}

// ---------------------------------------------------------------------------
// Describing a schedule
// ---------------------------------------------------------------------------

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ORDINAL_NAMES: Record<number, string> = { 1: 'first', 2: 'second', 3: 'third', 4: 'fourth', [-1]: 'last' };

export function weekdayName(date: string): string {
  return WEEKDAY_NAMES[weekdayOf(date)];
}

/** "Every Tuesday", "Every other Tuesday", "Every 4 weeks on Tuesday", "Second Tuesday of the month". */
export function describeFrequency(frequency: RecurrenceFrequency, anchorDate: string): string {
  const day = weekdayName(anchorDate);
  switch (frequency) {
    case 'weekly':
      return `Every ${day}`;
    case 'fortnightly':
      return `Every other ${day}`;
    case 'four_weekly':
      return `Every 4 weeks on ${day}`;
    case 'monthly': {
      const ord = ORDINAL_NAMES[monthlyOrdinal(anchorDate)];
      return `${ord.charAt(0).toUpperCase()}${ord.slice(1)} ${day} of the month`;
    }
  }
}

/** "Every Tuesday at 09:00" (times are HH:mm or HH:mm:ss). */
export function describeSchedule(frequency: RecurrenceFrequency, anchorDate: string, startTime: string): string {
  return `${describeFrequency(frequency, anchorDate)} at ${startTime.slice(0, 5)}`;
}

/** Minutes between two HH:mm(:ss) times; 0 if the end isn't after the start. */
export function minutesBetween(start: string, end: string): number {
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  return Math.max(0, toMin(end) - toMin(start));
}

/** HH:mm plus minutes, capped at 23:59. */
export function addMinutesToTime(time: string, minutes: number): string {
  const total = Math.min(23 * 60 + 59, Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5)) + minutes);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// Generation plan (mirrors _recurring_extend_one)
// ---------------------------------------------------------------------------

export interface ExistingVisit {
  id: string;
  series_occurrence_date: string;
  scheduled_date: string;
  status: string;
  is_exception: boolean;
}

export interface SkipRecord {
  occurrence_date: string;
  reason: 'skipped' | 'clash';
}

export interface GenerationPlan {
  /** Dates that get a new visit. */
  create: string[];
  /** Dates that would double-book and are flagged instead. */
  clashes: string[];
}

/**
 * Which dates in [from, to] a run would fill: dates already holding a visit
 * (including moved/edited ones, by their original pattern date) and skipped
 * dates are left alone; clashing dates are flagged, never double-booked.
 */
export function planGeneration(
  rule: RecurrenceRule,
  existing: Pick<ExistingVisit, 'series_occurrence_date'>[],
  skips: SkipRecord[],
  from: string,
  to: string,
  clashesOn: (date: string) => boolean = () => false,
): GenerationPlan {
  const taken = new Set(existing.map((v) => v.series_occurrence_date));
  const skipped = new Set(skips.filter((s) => s.reason === 'skipped').map((s) => s.occurrence_date));
  const plan: GenerationPlan = { create: [], clashes: [] };
  for (const d of occurrenceDates(rule, from, to)) {
    if (taken.has(d) || skipped.has(d)) continue;
    if (clashesOn(d)) plan.clashes.push(d);
    else plan.create.push(d);
  }
  return plan;
}

// ---------------------------------------------------------------------------
// Edit scope
// ---------------------------------------------------------------------------

export type EditScope = 'this' | 'future';

/** Fields whose change makes a visit "edited" (detached from the pattern). */
export const SCHEDULE_FIELDS = [
  'scheduled_date',
  'start_time',
  'end_time',
  'team_member_id',
  'service_id',
  'address',
  'title',
] as const;

/** True when an edit would detach the visit (mirrors the recurring_mark_job_exception trigger). */
export function isScheduleChange(
  before: Partial<Record<(typeof SCHEDULE_FIELDS)[number], string | null>>,
  after: Partial<Record<(typeof SCHEDULE_FIELDS)[number], string | null>>,
): boolean {
  const norm = (v: string | null | undefined, key: string) =>
    v == null || v === '' ? null : key.endsWith('_time') ? v.slice(0, 5) : v;
  return SCHEDULE_FIELDS.some((key) => key in after && norm(after[key], key) !== norm(before[key], key));
}

/** A visit still exactly as generated, so regeneration may replace it. */
export function isRegenerable(visit: Pick<ExistingVisit, 'status' | 'is_exception'>): boolean {
  return !visit.is_exception && (visit.status === 'scheduled' || visit.status === 'confirmed');
}

export interface FutureEditPlan {
  /** Later visits that will be replaced on the new pattern. */
  replace: string[];
  /** Later visits left exactly as they are (edited, completed, cancelled, in progress). */
  keep: string[];
}

/**
 * "This and all future visits": which later visits of the series get replaced
 * and which are kept. Earlier visits and anything before today are never
 * touched. Mirrors update_recurring_series_from_visit.
 */
export function planFutureEdit(visits: ExistingVisit[], edited: ExistingVisit, today: string): FutureEditPlan {
  const plan: FutureEditPlan = { replace: [], keep: [] };
  for (const v of visits) {
    if (v.id === edited.id) continue;
    if (v.series_occurrence_date <= edited.series_occurrence_date) continue;
    if (v.scheduled_date < today) continue;
    (isRegenerable(v) ? plan.replace : plan.keep).push(v.id);
  }
  return plan;
}

/** Whether a visit can be edited for "this and all future" at all. */
export function canEditFuture(visit: Pick<ExistingVisit, 'status'>, seriesStatus: SeriesStatus | null | undefined): boolean {
  return seriesStatus !== 'ended' && visit.status !== 'completed' && visit.status !== 'cancelled';
}

/** Whether a visit can be skipped (not once it has started). */
export function canSkipVisit(visit: Pick<ExistingVisit, 'status'>): boolean {
  return visit.status !== 'completed' && visit.status !== 'in_progress';
}

export const SERIES_STATUS_LABELS: Record<SeriesStatus, string> = {
  pending: 'Awaiting approval',
  active: 'Active',
  paused: 'Paused',
  ended: 'Ended',
};
